import { useState, useEffect } from 'react'
import { useWatch } from '../contexts/WatchContext'
import { useAuth } from '../contexts/AuthContext'
import screenService from '@/utils/services/ScreenService'

export function useWatchedScreenService() {
  const { getEffectiveUserId, isWatching, watchedUser } = useWatch()
  const { currentUserData } = useAuth()
  const [screens, setScreens] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const loadScreens = async (useCache = true) => {
    const userId = getEffectiveUserId()
    
    if (!userId) {
      setScreens([])
      setLoading(false)
      return
    }

    try {
      setLoading(true)
      setError(null)
      const userScreens = await screenService.getUserScreens(userId, useCache)
      setScreens(userScreens)
    } catch (err) {
      console.error('❌ Error loading screens:', err)
      setError(err)
      setScreens([])
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    const userId = getEffectiveUserId();
    if (!userId) {
      return;
    }
    loadScreens(true) // useCache = true
  }, [isWatching, watchedUser?.key, currentUserData?.key])

  const refreshScreens = () => {
    loadScreens(false) // Force fresh data
  }

  const getScreen = (screenId) => {
    if (!screenId || !screens || screens.length === 0) return null
    return screens.find(s => s && s.key === screenId) || null
  }
  
  const getDefaultScreen = () => {
    if (!screens || screens.length === 0) return null
    return screens.find(s => s && s.isDefault === true) || null
  }
  
  const getScreenName = (screenId) => {
    if (!screenId) return 'No Screen Selected'
    const screen = getScreen(screenId)
    if (screen && screen.name) return screen.name
    const defaultScreen = getDefaultScreen()
    return defaultScreen ? defaultScreen.name : 'No Default Screen'
  }

  const getScreenThumb = (screen) => {
    if (!screen || typeof screen !== 'object') return null
    
    // For video content - prioritize thumbnail URLs
    if (screen.videoUrl) {
      // Check for existing thumbnail URLs first
      if (screen.thumbUniversalUrl) return screen.thumbUniversalUrl
      if (screen.thumbUrl) return screen.thumbUrl
      
      // For video URLs, try to generate thumbnail URL
      if (screen.videoUrl) {
        // Handle different video URL patterns
        if (screen.videoUrl.includes('_video') && !screen.videoUrl.includes('_video_thumb')) {
          return screen.videoUrl.replace('_video', '_video_thumb')
        }
        // For other video URLs, try to create thumbnail URL
        if (screen.videoUrl.includes('.mp4')) {
          return screen.videoUrl.replace(/\.mp4$/, '_640x360.jpeg')
        }
        // Fallback to video URL itself
        return screen.videoUrl
      }
    }
    
    // For slides content - get first slide
    if (screen.slides && screen.slidesData && screen.slidesData[0]) {
      const firstSlide = screen.slidesData[0]
      
      // Check for video in slide
      if (firstSlide.videoUrl) {
        if (firstSlide.thumbUniversalUrl) return firstSlide.thumbUniversalUrl
        if (firstSlide.thumbUrl) return firstSlide.thumbUrl
        if (firstSlide.videoUrl.includes('_video') && !firstSlide.videoUrl.includes('_video_thumb')) {
          return firstSlide.videoUrl.replace('_video', '_video_thumb')
        }
        if (firstSlide.videoUrl.includes('.mp4')) {
          return firstSlide.videoUrl.replace(/\.mp4$/, '_640x360.jpeg')
        }
        return firstSlide.videoUrl
      }
      
      // Check for image in slide
      if (firstSlide.imageUniversalUrl) return firstSlide.imageUniversalUrl
      if (firstSlide.imageUrl) return firstSlide.imageUrl
      if (firstSlide.thumbUniversalUrl) return firstSlide.thumbUniversalUrl
      if (firstSlide.thumbUrl) return firstSlide.thumbUrl
    }
    
    // For regular image content
    if (screen.imageUrl) {
      if (screen.imageUniversalUrl) return screen.imageUniversalUrl
      return screen.imageUrl
    }
    
    return null
  }

  const getScreenThumbById = (screenId) => {
    const screen = getScreen(screenId)
    if (!screen) {
      const defaultScreen = getDefaultScreen()
      if (defaultScreen) {
        const thumbUrl = getScreenThumb(defaultScreen)
        if (thumbUrl) {
          return getOptimizedThumbnailUrl(thumbUrl)
        }
      }
      return null
    }
    
    const thumbUrl = getScreenThumb(screen)
    if (!thumbUrl) return null
    return getOptimizedThumbnailUrl(thumbUrl)
  }

  // Helper function to optimize thumbnail URLs
  const getOptimizedThumbnailUrl = (url) => {
    if (!url || typeof url !== 'string') return null
    
    // For Cloudflare URLs, return as-is since they handle thumbnails differently
    if (url.includes('r2.dev') || url.includes('cloudflare')) {
      return url
    }
    
    // For Firebase URLs, convert to thumbnail size
    let optimizedUrl = url
      .replace("_1920x1080.", "_640x360.")
      .replace("_1920x1920.", "_640x640.")
      .replace("_1080x1920.", "_360x640.")
    
    // Handle video thumbnails
    if (url.includes('_video') && !url.includes('_video_thumb')) {
      optimizedUrl = url.replace('_video', '_video_thumb')
    }
    
    // Handle video file extensions
    if (url.includes('.mp4')) {
      optimizedUrl = url.replace(/\.mp4$/, '_640x360.jpeg')
    }
    
    return optimizedUrl
  }

  return {
    screens,
    loading,
    error,
    getScreen,
    getDefaultScreen,
    getScreenName,
    getScreenThumb,
    getScreenThumbById,
    refreshScreens,
    loadScreens
  }
} 